import React, { useEffect, useState } from "react";
import PasswordInput from "./PasswordInput";

import { API_BASE as API } from "../utils/apiBase";

function LinkButton({ href, onClick, className = "", children }) {
  // Looks like a button, behaves like a link (best of both worlds).
  return (
    <a
      href={href}
      onClick={(e) => {
        // If we have an onClick handler, we want SPA behavior.
        if (onClick) {
          e.preventDefault();
          onClick();
        }
      }}
      className={`block w-full border rounded-xl py-2 text-center hover:bg-gray-50 dark:hover:bg-gray-700 select-none cursor-pointer ${className}`}
      role="button"
    >
      {children}
    </a>
  );
}

export default function LoginForm({
  defaultEmail = "",
  notice = "",
  onLogin,
  onGoSignup,
  onGoVerify,
  onGoForgotPassword,
  onGoBack,
}) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  useEffect(() => {
    if (defaultEmail) setEmail(defaultEmail);
  }, [defaultEmail]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }

    try {
      setPending(true);

      const body = new URLSearchParams({
        username: email.trim().toLowerCase(),
        password,
      });

      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Login failed");

      // Two-factor: switch to the code step instead of finishing login.
      if (data?.mfa_required && data?.mfa_token) {
        setMfaToken(data.mfa_token);
        setMfaCode("");
        return;
      }

      if (data?.access_token) {
        try {
          localStorage.setItem("token", data.access_token);
        } catch {}
      }
      onLogin && onLogin(data?.access_token || null, { enrollMfa: !!data?.mfa_enrollment_required });
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setPending(false);
    }
  }

  async function verifyMfa(e) {
    e.preventDefault();
    setError("");
    try {
      setPending(true);
      const res = await fetch(`${API}/auth/mfa/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mfa_token: mfaToken, code: mfaCode.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Verification failed");
      if (data?.access_token) {
        try {
          localStorage.setItem("token", data.access_token);
        } catch {}
      }
      onLogin && onLogin(data?.access_token || null);
    } catch (err) {
      setError(err.message || "Verification failed");
    } finally {
      setPending(false);
    }
  }

  const emailParam = encodeURIComponent(email || "");
  const hrefVerify = `/verify?email=${emailParam}`;
  const hrefForgot = `/forgot?email=${emailParam}`;
  const hrefSignup = `/signup`;
  const hrefHome = `/`;

  const goSignup = () => (onGoSignup ? onGoSignup() : window.location.assign(hrefSignup));
  const goVerify = () => (onGoVerify ? onGoVerify(email) : window.location.assign(hrefVerify));
  const goForgot = () =>
    (onGoForgotPassword ? onGoForgotPassword(email) : window.location.assign(hrefForgot));
  const goBack = () => {
    if (onGoBack) return onGoBack();
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    window.location.assign(hrefHome);
  };

  if (mfaToken) {
    return (
      <form onSubmit={verifyMfa} className="max-w-sm mx-auto p-4 space-y-4">
        <h2 className="text-2xl font-semibold">Two-factor verification</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Enter the 6-digit code from your authenticator app. You can also use one of your recovery codes.
        </p>
        <input
          inputMode="numeric"
          autoComplete="one-time-code"
          className="w-full border rounded p-2 tracking-widest"
          placeholder="123456"
          value={mfaCode}
          onChange={(e) => setMfaCode(e.target.value)}
          autoFocus
          required
        />
        {error && <div className="text-red-600 dark:text-red-400">{error}</div>}
        <button
          type="submit"
          disabled={pending || !mfaCode.trim()}
          className="w-full bg-blue-600 text-white rounded-xl py-2 disabled:opacity-60"
        >
          {pending ? "Verifying…" : "Verify"}
        </button>
        <button
          type="button"
          onClick={() => {
            setMfaToken("");
            setMfaCode("");
            setError("");
          }}
          className="block w-full border rounded-xl py-2 text-center hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Back to login
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-sm mx-auto p-4 space-y-4">
      <h2 className="text-2xl font-semibold">Log in</h2>

      {notice ? (
        <div
          role="status"
          className="rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-gray-900 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
        >
          {notice}
        </div>
      ) : null}

      <input
        type="email"
        className="w-full border rounded p-2"
        placeholder="Email"
        autoComplete="username"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />

      <PasswordInput
        className="w-full border rounded p-2"
        placeholder="Password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
      />

      {error && <div className="text-red-600 dark:text-red-400">{error}</div>}

      <button
        type="submit"
        disabled={pending || !email || !password}
        className="w-full bg-blue-600 text-white rounded-xl py-2 disabled:opacity-60"
      >
        {pending ? "Logging in…" : "Log in"}
      </button>

      {/* Use link-style buttons so they're always clickable */}
      <LinkButton href={hrefForgot} onClick={goForgot}>
        Forgot password
      </LinkButton>

      <div className="grid grid-cols-2 gap-2">
        <LinkButton href={hrefSignup} onClick={goSignup}>
          Create account
        </LinkButton>

        <LinkButton href={hrefVerify} onClick={goVerify}>
          Verify email
        </LinkButton>

        <button
          type="button"
          onClick={goBack}
          className="border rounded-xl py-2 text-center hover:bg-gray-50 dark:hover:bg-gray-700 inline-flex items-center justify-center gap-2"
          title="Back"
          aria-label="Back"
        >
          <i className="fi fi-rr-angle-left text-lg leading-none not-italic" />
        </button>

        <div />
      </div>
    </form>
  );
}
